/* eslint-disable spellcheck/spell-checker */

import getFormTransformers from './getForm';
import { getIsoStringFromDateTime } from './helpers';
import { APSV2Form, FieldReportStatus, FormStatuses } from '../../types';

export interface UpdateFormV2RequestBody {
  assigneeId?: string;
  assigneeType?: 'user' | 'company' | 'role';
  description?: string;
  dueDate?: string;
  formDate?: string;
  locationId?: string;
  notes?: string;
  submitterSignature?: string;
  status?: FormStatuses;
  name?: string | null;
}

/* 
Translate Cacahute API query params to satisfy APS query params
method name: updateFieldReport
Cacahute API: /v8/project/${projectId}/field-report-types/${formTemplateId}/reports/${formId}
APS API: /development/v2/projects/${projectId}/form-templates/${formTemplateId}/forms/${formId}
*/
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const translateBody = (body: Record<string, any>): UpdateFormV2RequestBody => {
  const apsParams: UpdateFormV2RequestBody = {
    ...body,
  };

  // simple key mapping
  const keyMapping: Record<string, keyof UpdateFormV2RequestBody> = {
    report_date: 'formDate',
    location_id: 'locationId',
    description: 'notes',
    assignee_id: 'assigneeId',
    assignee_type: 'assigneeType',
    title: 'description',
    due_date: 'dueDate',
    last_submitter_signature: 'submitterSignature',
  };
  const nullableKeys: (keyof UpdateFormV2RequestBody)[] = [
    'locationId',
    'dueDate',
  ];
  type ValueMappingRecord = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [K in keyof UpdateFormV2RequestBody]: (oldValue: any) => unknown;
  };
  // An object of value translation functions: key is the new query param key, the value is the translation function
  const valueMapping: ValueMappingRecord = {
    formDate: (date: string) => {
      return getIsoStringFromDateTime(date)?.substring(0, 10);
    },
    dueDate: (date: string | null) => {
      return date ? getIsoStringFromDateTime(date)?.substring(0, 10) : null;
    },
    status: (oldValue: FieldReportStatus) => {
      const statusMap: Record<FieldReportStatus, string> = {
        draft: 'inProgress',
        in_review: 'inReview',
        submitted: 'closed',
        archived: 'archived',
        discarded: 'discarded',
      };
      return statusMap[oldValue] || oldValue;
    },
    name: (oldValue: string | null) => {
      return oldValue || '';
    },
  };

  // translate keys/values
  Object.entries(apsParams).forEach(([key, value]) => {
    const newKey = keyMapping[key] || key;
    // remove old params from cacahuete, type conversion is just here for typescript
    delete apsParams[key as unknown as keyof UpdateFormV2RequestBody];

    const newValue = valueMapping[newKey]
      ? valueMapping[newKey]?.(value)
      : value;

    if (newValue !== null || nullableKeys.includes(newKey)) {
      apsParams[newKey] = newValue;
    }
  });

  return apsParams;
};

// translate APS response to Cacahuete, this should be only temporary until we fully migrate to APS
const translateResponse = (
  form: APSV2Form,
  metadata: {
    projectId: string;
    signedPDFUrls: { fileUrl: string; thumbnailUrl: string };
  },
) => {
  return getFormTransformers.translateResponse(form, null, metadata);
};

const updateFormTransformers = {
  translateBody,
  translateResponse,
};

export default updateFormTransformers;
